import {
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  onAuthStateChanged,
  signOut,
  type User,
} from 'firebase/auth';
import { getFirebase } from '@/firebase';

const EMAIL_CACHE_KEY = 'rta.systems.signInEmail';

// Magic-link send. The founder lands on the sign-in card, submits their
// email, and a link arrives. Clicking it redirects to /auth/callback.
export async function requestMagicLink(email: string): Promise<void> {
  const fb = getFirebase();
  if (!fb) throw new Error('Firebase not configured');
  const url =
    import.meta.env.VITE_MAGIC_LINK_REDIRECT_URL ??
    `${window.location.origin}/auth/callback`;
  await sendSignInLinkToEmail(fb.auth, email, {
    url,
    handleCodeInApp: true,
  });
  window.localStorage.setItem(EMAIL_CACHE_KEY, email);
}

// Called on /auth/callback mount — consumes the magic-link hash and returns
// the authed user. Null when the URL isn't a magic link.
export async function completeMagicLinkSignIn(): Promise<User | null> {
  const fb = getFirebase();
  if (!fb) return null;
  if (!isSignInWithEmailLink(fb.auth, window.location.href)) return null;

  let email = window.localStorage.getItem(EMAIL_CACHE_KEY);
  if (!email) {
    email = window.prompt('Please confirm your email for verification.') ?? null;
    if (!email) return null;
  }
  const result = await signInWithEmailLink(fb.auth, email, window.location.href);
  window.localStorage.removeItem(EMAIL_CACHE_KEY);
  return result.user;
}

export function observeAuth(cb: (user: User | null) => void): () => void {
  const fb = getFirebase();
  if (!fb) {
    cb(null);
    return () => {};
  }
  return onAuthStateChanged(fb.auth, cb);
}

export async function signOutUser(): Promise<void> {
  const fb = getFirebase();
  if (!fb) return;
  await signOut(fb.auth);
}

// Normalized founder-email check. Used by FlagGate and anywhere a route
// needs to assert "founder only".
const FOUNDER_EMAIL =
  import.meta.env.VITE_FOUNDER_EMAIL ?? 'rushangbhardwaj@outlook.com';

export function isFounder(user: User | null | undefined): boolean {
  if (!user?.email) return false;
  return user.email.toLowerCase() === FOUNDER_EMAIL.toLowerCase();
}

export function founderEmail(): string {
  return FOUNDER_EMAIL;
}
